import {GameBoard, GameConfig, GameData, GamePlay, GamePlayKey, GameSetup} from "../types";
import {v4 as uuid} from "uuid";

export class NonogramLayout {

  readonly rows: number[][] = [];
  readonly cols: number[][] = [];

  private maxRowLength: number = 0;
  private maxColLength: number = 0;

  constructor(readonly board: number[], readonly size: number) {
    for (let i = 0; i < size; i++) {
      const row = [];
      let count: number = 0;
      for (let j = 0; j < size; j++) {
        if (board[i * size + j] > 0) {
          count++;
        } else if (count > 0) {
          row.push(count);
          count = 0;
        }
      }
      if (count > 0) {
        row.push(count);
      }
      if (row.length > this.maxRowLength) {
        this.maxRowLength = row.length;
      }
      this.rows.push(row);
    }
    for (let i = 0; i < size; i++) {
      const col = [];
      let count: number = 0;
      for (let j = 0; j < size; j++) {
        if (board[i + j * size] > 0) {
          count++;
        } else if (count > 0) {
          col.push(count);
          count = 0;
        }
      }
      if (count > 0) {
        col.push(count);
      }
      if (col.length > this.maxColLength) {
        this.maxColLength = col.length;
      }
      this.cols.push(col);
    }
  }
  
  compareEquals(layout: NonogramLayout): boolean{
    const size = this.size;
    if (size !== layout.size){
      return false;
    }
    for (let i = 0; i < size; i++) {
      if (this.rows[i].length !== layout.rows[i].length || this.cols[i].length !== layout.cols[i].length) {
        return false;
      }
      for (let j = 0; j < this.rows[i].length; j++) {
        if (this.rows[i][j] !== layout.rows[i][j]) {
          return false;
        }
      }
      for (let j = 0; j < this.cols[i].length; j++) {
        if (this.cols[i][j] !== layout.cols[i][j]) {
          return false;
        }
      }
    }
    return true;
  }

  getMaxRowLength(): number {
    return this.maxRowLength;
  }

  getMaxColLength(): number {
    return this.maxColLength;
  }
}

export class NonogramDifficultyLevel {

  static readonly VALUES = new Array<NonogramDifficultyLevel>(4);
  static readonly EASY = NonogramDifficultyLevel.VALUES[0] = new NonogramDifficultyLevel("easy", "Easy", "Easy desc", 4);
  static readonly MEDIUM = NonogramDifficultyLevel.VALUES[1] = new NonogramDifficultyLevel("medium", "Medium", "Medium desc", 6);
  static readonly HARD = NonogramDifficultyLevel.VALUES[2] = new NonogramDifficultyLevel("hard", "Hard", "Hard desc", 8);
  static readonly VERY_HARD = NonogramDifficultyLevel.VALUES[3] = new NonogramDifficultyLevel("very_hard", "Very Hard", "Very Hard desc", 10);

  constructor(readonly name: string, readonly text: string, readonly description: string, readonly size: number) {
  }
}

export class NonogramGameConfig extends GameConfig {

  constructor(readonly level: NonogramDifficultyLevel) {
    super();
  }
}

export class NonogramGameSetup extends GameSetup {

  constructor(key: GamePlayKey) {
    super(key);
  }
}

export class NonogramGameData extends GameData<NonogramGameBoard> {

  static async create(config: NonogramGameConfig): Promise<NonogramGameData> {
    const loader = new NonogramGamesLoader();
    const layout = await loader.getRandomGame(config.level);
    const setup = new NonogramGameSetup(new GamePlayKey("nonogram", uuid()));
    const now = Date.now();
    return new NonogramGameData(new GamePlay(setup, now, 0, 0, 0, new NonogramGameBoard(config.level, layout).toJSON()));
  }
}

export class NonogramGamesLoader {
  constructor() {
  }

  async getRandomGame(difficultyLevel: NonogramDifficultyLevel): Promise<NonogramLayout> {
    const size = difficultyLevel.size;
    const board = new Array<number>(size * size);
    for (let i = 0; i < size * size; i++) {
      board[i] = 0;
    }
    let validIndices: number[] = [];
    for (let i = 0; i < size * size; i++) {
      validIndices[i] = i;
    }
    const filled = size * size / 2;
    for (let i = 0; i < filled; i++) {
      board[validIndices.splice(Math.floor(validIndices.length * Math.random()), 1)[0]] = 1;
    }
    return new NonogramLayout(board, size);
  }
}

export class NonogramGameBoard extends GameBoard {

  // Max duration to earn time-based points
  private static readonly POINTS_MAX_DURATION = 3 * 60 * 60 * 1000; // 3 hours

  readonly board;
  readonly checked;

  constructor(readonly level: NonogramDifficultyLevel, readonly layout: NonogramLayout) {
    super();
    this.board = new Array<number>(layout.size * layout.size);
    this.board.fill(-1, 0, layout.size * layout.size);
    this.checked = new Array<number>(layout.size * layout.size);
    this.checked.fill(0, 0, layout.size * layout.size);
  }

  getPoints(duration: number): number {
    return 0;
    // const values = NonogramDifficultyLevel.VALUES;
    // const min = values[0].pointsFactor;
    // const max = values[values.length - 1].pointsFactor;
    // const timePoints = Math.floor(50 * Math.max(0, 1 - duration / NonogramGameBoard.POINTS_MAX_DURATION));
    // const pointsFactorPoints = Math.floor(50 * (this.level.pointsFactor - min) / (max - min));
    // return Math.max(0, timePoints + pointsFactorPoints);
  }

  toJSON(): any {
    return this;
  }
}